import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@src/context";
import { LoadingState } from "@components/LoadingState";
import ProfileCompletionOverlay from "./ProfileCompletionOverlay";

/**
 * ProfileGuard component that shows an overlay for logged-in users without profiles
 * Only allows access to profile creation pages, admin routes, and logout if profile doesn't exist
 */
const ProfileGuard = ({ children, allowedWithoutProfile = [] }) => {
    const { userData, LoginData, isLoading, isLogin, role } = useAuth();
    const location = useLocation();

    // Show loading while auth is loading
    if (isLoading) {
        return <LoadingState />;
    }

    // Only apply profile check to logged-in users
    if (!isLogin || !LoginData) {
        return <>{children}</>;
    }

    // Skip profile check for admin users
    if (role === 'admin') {
        return <>{children}</>;
    }

    // Wait for userData to load
    if (!userData) {
        return <LoadingState />;
    }

    // Check if current path is allowed without profile
    const isAllowedPath = allowedWithoutProfile.some(path => 
        location.pathname === path || location.pathname.startsWith(path + '/')
    );

    const userRole = LoginData?.role;
    const passwordChangePath = "/employer-edit-password";

    // Check if user is a company user with pending status - redirect to password change (priority check)
    if (userRole === "employer" && userData?.is_company_staff && userData?.company_user_status === "pending") {
        // Allow access to password change page and logout
        if (location.pathname === passwordChangePath || location.pathname === "/logout") {
            return <>{children}</>;
        }
        // Redirect to password change page
        return <Navigate to={passwordChangePath} replace />;
    }

    // Check if user has profile based on their role
    let hasProfile = false;

    if (userRole === "candidate") {
        hasProfile = !!(userData?.user_profile);
    } else if (userRole === "employer") {
        hasProfile = !!(userData?.employer_profile);
    }

    // If profile doesn't exist
    if (!hasProfile) {
        // If on allowed path (logout or profile creation), allow it
        if (isAllowedPath) {
            return <>{children}</>;
        }
        
        // Show overlay instead of redirecting
        return <ProfileCompletionOverlay>{children}</ProfileCompletionOverlay>;
    }

    // Profile exists, allow access
    return <>{children}</>;
};

export default ProfileGuard;

